export const DECISION_STEP=.25;
export const DECISION_DEFAULTS=Object.freeze({playerX:4.7,playerY:2,fov:100,volume:.7,memory:3,health:100,hunger:25,cover:true,autoMove:false});
const clamp=(n,min,max)=>Math.max(min,Math.min(max,n));
const clone=value=>JSON.parse(JSON.stringify(value));
const agent={x:1.1,y:2,heading:0},supply={x:1.2,y:3.35},solid={x:2.75,y:1.3,w:.6,h:1.4};
const actions=['retreat','chase','recover','search','patrol'];

function occluded(a,b){
 let lo=0,hi=1;
 for(const [start,delta,min,max] of [[a.x,b.x-a.x,solid.x,solid.x+solid.w],[a.y,b.y-a.y,solid.y,solid.y+solid.h]]){
  if(Math.abs(delta)<1e-9){if(start<min||start>max)return false;continue;}
  const p=(min-start)/delta,q=(max-start)/delta;lo=Math.max(lo,Math.min(p,q));hi=Math.min(hi,Math.max(p,q));
  if(lo>hi)return false;
 }
 return true;
}
function destination(action,known){
 if(action==='chase'||action==='search')return {x:known.x,y:known.y};
 if(action==='recover')return {...supply};
 if(action==='retreat'){
  const dx=agent.x-known.x,dy=agent.y-known.y,len=Math.hypot(dx,dy)||1;
  return {x:clamp(agent.x+dx/len*1.2,.2,5.8),y:clamp(agent.y+dy/len*1.2,.2,3.8)};
 }
 return {x:1.1,y:.6};
}

export function compareDecisions(observation,config){
 const {visible,known,age}=observation,h=config.health/100,u=config.hunger/100;
 const conditions=[Boolean(known)&&h<.3,visible,u>.65,Boolean(known),true];
 let selected=false;
 const trace=actions.map((action,i)=>{
  const status=selected?'skipped':conditions[i]?'selected':'failed';if(status==='selected')selected=true;
  return {action,status};
 });
 const priorityAction=trace.find(row=>row.status==='selected').action;
 // Only the shared observation may supply threat distance. Hidden world positions are excluded.
 const proximity=known?clamp(1-Math.hypot(known.x-agent.x,known.y-agent.y)/6,0,1):0;
 const freshness=known?clamp(1-age/Math.max(.001,config.memory),0,1):0;
 const values=[(1-h)*80+proximity*10,45+h*35-u*25,u*85+(1-h)*15,40*freshness,12];
 const eligible=[Boolean(known),visible,true,Boolean(known)&&!visible,true];
 const scores=actions.map((action,i)=>({action,eligible:eligible[i],score:eligible[i]?values[i]:0}));
 const winner=scores.filter(row=>row.eligible).reduce((a,b)=>b.score>a.score?b:a);
 return {priority:{action:priorityAction,target:destination(priorityAction,known),trace},utility:{action:winner.action,target:destination(winner.action,known),scores}};
}

export class DecisionModel{
 constructor(config={}){this.reset(config);}
 reset(config={}){
  this._config={...DECISION_DEFAULTS};this.time=0;this.tick=0;this.lastKnown=null;this.sound=null;this.history=[];
  this.configure(config);return this.state;
 }
 configure(patch={}){
  for(const [key,min,max] of [['playerX',.2,5.8],['playerY',.2,3.8],['fov',20,180],['volume',0,1],['memory',.25,8],['health',0,100],['hunger',0,100]]){
   if(Number.isFinite(patch[key]))this._config[key]=clamp(patch[key],min,max);
  }
  for(const key of ['cover','autoMove'])if(typeof patch[key]==='boolean')this._config[key]=patch[key];
  this._observe();return this.state;
 }
 movePlayer(x,y){return this.configure({playerX:x,playerY:y});}
 emitSound(){
  this.sound={x:this._config.playerX,y:this._config.playerY,time:this.time,radius:this._config.volume*6};
  this._observe(this.sound);this._record();return this.state;
 }
 step(){
  this.tick++;this.time=this.tick*DECISION_STEP;
  if(this._config.autoMove){this._config.playerX=4.5+Math.sin(this.time*.7)*.9;this._config.playerY=2+Math.sin(this.time)*1.6;}
  this._observe();this._record();return this.state;
 }
 _observe(sound=null){
  const player={x:this._config.playerX,y:this._config.playerY},distance=Math.hypot(player.x-agent.x,player.y-agent.y);
  const angle=Math.atan2(player.y-agent.y,player.x-agent.x);
  const reason=distance>6?'out-of-range':Math.abs(angle)>this._config.fov*Math.PI/360?'outside-fov':this._config.cover&&occluded(agent,player)?'occluded':'visible';
  const visible=reason==='visible',heard=Boolean(sound&&Math.hypot(sound.x-agent.x,sound.y-agent.y)<=sound.radius&&sound.radius>0);
  if(visible)this.lastKnown={...player,time:this.time,source:'vision'};
  else if(heard)this.lastKnown={x:sound.x,y:sound.y,time:this.time,source:'hearing'};
  if(this.lastKnown&&this.time-this.lastKnown.time>this._config.memory)this.lastKnown=null;
  this.observation={visible,heard,reason,known:this.lastKnown?{...this.lastKnown}:null,age:this.lastKnown?this.time-this.lastKnown.time:null};
  this.decisions=compareDecisions(this.observation,this._config);
 }
 _record(){this.history.push({tick:this.tick,time:this.time,priority:this.decisions.priority.action,utility:this.decisions.utility.action,visible:this.observation.visible,heard:this.observation.heard,known:Boolean(this.lastKnown)});if(this.history.length>32)this.history.shift();}
 get state(){return clone({time:this.time,tick:this.tick,config:this._config,world:{width:6,height:4,agent,supply,solid:this._config.cover?solid:null,player:{x:this._config.playerX,y:this._config.playerY}},observation:this.observation,...this.decisions,sound:this.sound,history:this.history});}
}
